import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from 'react'
import LightboxZoomImage from './LightboxZoomImage'
import type { ListingDetailDefectPin } from './ListingDetailDefectPins'

type Props = {
  sources: string[]
  activePhoto: number
  onActivePhotoChange: (index: number) => void
  onZoomChange?: (zoomed: boolean) => void
  interactionLocked?: boolean
  defectMarks?: ListingDetailDefectPin[]
}

const IOS_GALLERY_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'
const SNAP_RATIO = 0.2
const SNAP_MIN_PX = 44

export default function ListingDetailLightboxCarousel({
  sources,
  activePhoto,
  onActivePhotoChange,
  onZoomChange,
  interactionLocked = false,
  defectMarks = [],
}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [dragPx, setDragPx] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef<{ x: number; pointerId: number } | null>(null)
  const activePhotoRef = useRef(activePhoto)
  activePhotoRef.current = activePhoto
  /** Avoid animating from slide 0 when viewport width is first measured. */
  const [trackTransitionReady, setTrackTransitionReady] = useState(false)

  useLayoutEffect(() => {
    if (viewportWidth <= 0) {
      setTrackTransitionReady(false)
      return
    }
    setTrackTransitionReady(false)
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setTrackTransitionReady(true))
    })
    return () => cancelAnimationFrame(id)
  }, [viewportWidth, sources.length])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const measure = () => setViewportWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const finishDrag = useCallback(
    (dx: number) => {
      const w = viewportWidth
      const current = activePhotoRef.current
      setIsDragging(false)
      setDragPx(0)
      if (w <= 0 || interactionLocked) return
      const threshold = Math.max(SNAP_MIN_PX, w * SNAP_RATIO)
      let next = current
      if (dx <= -threshold) next = Math.min(sources.length - 1, current + 1)
      else if (dx >= threshold) next = Math.max(0, current - 1)
      if (next !== current) onActivePhotoChange(next)
    },
    [viewportWidth, sources.length, onActivePhotoChange, interactionLocked],
  )

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    if (e.target instanceof HTMLElement && e.target.closest('.listing-detail-defect-pin-wrap')) return
    if (interactionLocked || sources.length <= 1) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    dragStartRef.current = { x: e.clientX, pointerId: e.pointerId }
    setIsDragging(true)
    setDragPx(0)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    const start = dragStartRef.current
    if (!start || start.pointerId !== e.pointerId || interactionLocked) return
    let dx = e.clientX - start.x
    const current = activePhotoRef.current
    if (current === 0 && dx > 0) dx *= 0.28
    if (current === sources.length - 1 && dx < 0) dx *= 0.28
    setDragPx(dx)
  }

  function endPointer(e: PointerEvent<HTMLDivElement>) {
    const start = dragStartRef.current
    if (!start || start.pointerId !== e.pointerId) return
    dragStartRef.current = null
    const dx = e.clientX - start.x
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
    finishDrag(dx)
  }

  const trackStyle: CSSProperties = {
    transform: `translate3d(${-activePhoto * viewportWidth + dragPx}px, 0, 0)`,
    transition:
      isDragging || interactionLocked || !trackTransitionReady
        ? 'none'
        : `transform 0.48s ${IOS_GALLERY_EASE}`,
  }

  return (
    <div className="listing-detail-lightbox-carousel">
      <div
        ref={viewportRef}
        className="listing-detail-lightbox-carousel__viewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        <div className="listing-detail-lightbox-carousel__track" style={trackStyle}>
          {sources.map((src, idx) => (
            <div key={`${src}-${idx}`} className="listing-detail-lightbox-slide">
              <LightboxZoomImage
                src={src}
                photoKey={`${idx}-${activePhoto}`}
                onZoomChange={idx === activePhoto ? onZoomChange : undefined}
                className="listing-detail-lightbox-img"
                defectPins={defectMarks.filter((m) => (m.photo_index ?? 0) === idx)}
                allDefectMarks={defectMarks}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
